import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { streamUrl, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Edit3, Save, X, MessageCircle, Send, User, DollarSign, Megaphone, Eye, Trash2 } from "lucide-react";

export default function VideoDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [video, setVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [editing, setEditing] = useState(false);
    const [budget, setBudget] = useState("");
    const [fee, setFee] = useState("");
    const [saving, setSaving] = useState(false);

    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [guestName, setGuestName] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);

    const loadVideo = useCallback(async () => {
        try {
            const { data } = await api.get(`/videos/${id}`);
            setVideo(data);
            setBudget(data.ad_budget || "");
            setFee(data.ad_fee || "");
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to load video");
        } finally {
            setLoading(false);
        }
    }, [id]);

    const loadComments = useCallback(async () => {
        try {
            const { data } = await api.get(`/videos/${id}/comments`);
            setComments(data);
        } catch {
            setComments([]);
        }
    }, [id]);

    useEffect(() => {
        loadVideo();
        loadComments();
    }, [loadVideo, loadComments]);

    const isOwner = user && user !== false && video && user.id === video.owner_id;

    const onSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/videos/${id}`, { ad_budget: budget, ad_fee: fee });
            setVideo(data);
            setEditing(false);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const onAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        setSubmittingComment(true);
        try {
            const body = { text: newComment.trim() };
            if (!user || user === false) body.name = guestName.trim() || "guest";
            const { data } = await api.post(`/videos/${id}/comments`, body);
            setComments((prev) => [data, ...prev]);
            setNewComment("");
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || "Failed to comment");
        } finally {
            setSubmittingComment(false);
        }
    };

    const onDeleteComment = async (cid) => {
        try {
            await api.delete(`/comments/${cid}`);
            setComments((prev) => prev.filter((c) => c.id !== cid));
        } catch {}
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div data-testid="video-detail-loading" className="font-heading font-bold text-lg text-ink/60">Loading video...</div>
            </div>
        );
    }

    if (!video) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div data-testid="video-not-found" className="brutal-card p-8 text-center bg-cream">
                    <h2 className="font-heading font-black text-2xl text-ink">Video not found</h2>
                    <p className="font-body text-ink/70 mt-2">{error || "It might have been removed."}</p>
                    <Link to="/" className="brutal-btn-primary inline-flex mt-6">Back to feed</Link>
                </div>
            </div>
        );
    }

    const AVATAR_COLORS = ["bg-vibrantyellow", "bg-vibrantpink", "bg-vibrantblue", "bg-vibrantorange"];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                {/* Main column */}
                <div className="lg:col-span-8 space-y-6">
                    {/* Player */}
                    <div data-testid="video-player-wrapper" className="relative w-full aspect-video bg-ink rounded-2xl border-[4px] border-ink overflow-hidden shadow-brutal-xl">
                        <video
                            data-testid="video-player"
                            src={streamUrl(video.id)}
                            controls
                            playsInline
                            className="w-full h-full"
                        />
                    </div>

                    {/* Title + meta */}
                    <div>
                        <h1 data-testid="video-title" className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-ink leading-tight">
                            {video.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 bg-vibrantpink border-[3px] border-ink rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" strokeWidth={3} />
                                </div>
                                <span data-testid="video-uploader" className="font-heading font-bold text-base text-ink">
                                    @{video.owner_username}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-white border-[3px] border-ink rounded-lg px-3 py-1 shadow-brutal">
                                <Eye className="w-4 h-4 text-ink" strokeWidth={2.5} />
                                <span data-testid="video-views" className="font-heading font-bold text-sm text-ink">{video.views} views</span>
                            </div>
                        </div>
                        {video.description && (
                            <p data-testid="video-description" className="font-body text-ink/80 mt-4 leading-relaxed whitespace-pre-line">
                                {video.description}
                            </p>
                        )}
                    </div>

                    {/* Advertising Budget & Fee */}
                    <div data-testid="ad-section" className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                        {/* Budget Card */}
                        <div className="bg-vibrantyellow border-[3px] border-ink rounded-2xl p-6 shadow-brutal-lg relative">
                            <div className="flex items-center gap-2 mb-2">
                                <Megaphone className="w-5 h-5 text-ink" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink">Video Uploader Budget</span>
                            </div>
                            {editing ? (
                                <input
                                    data-testid="ad-budget-input"
                                    className="brutal-input mt-2 bg-white"
                                    value={budget}
                                    onChange={(e) => setBudget(e.target.value)}
                                    placeholder="e.g. $500"
                                    maxLength={100}
                                />
                            ) : (
                                <div data-testid="ad-budget-value" className="font-heading font-black text-2xl sm:text-3xl text-ink tracking-tight">
                                    {video.ad_budget || <span className="text-ink/40">Not set</span>}
                                </div>
                            )}
                        </div>

                        {/* Fee Card */}
                        <div className="bg-vibrantblue text-white border-[3px] border-ink rounded-2xl p-6 shadow-brutal-lg relative">
                            <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-5 h-5 text-white" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs uppercase tracking-widest text-white">Payment for Advertising</span>
                            </div>
                            {editing ? (
                                <input
                                    data-testid="ad-fee-input"
                                    className="brutal-input mt-2 bg-white text-ink"
                                    value={fee}
                                    onChange={(e) => setFee(e.target.value)}
                                    placeholder="e.g. $50 / spot"
                                    maxLength={100}
                                />
                            ) : (
                                <div data-testid="ad-fee-value" className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
                                    {video.ad_fee || <span className="text-white/50">Not set</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Owner controls */}
                    {isOwner && (
                        <div data-testid="owner-controls" className="flex flex-wrap gap-3">
                            {editing ? (
                                <>
                                    <button
                                        onClick={onSave}
                                        disabled={saving}
                                        data-testid="ad-save-button"
                                        className="brutal-btn-primary"
                                    >
                                        <Save className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                        {saving ? "Saving..." : "Save changes"}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditing(false);
                                            setBudget(video.ad_budget || "");
                                            setFee(video.ad_fee || "");
                                        }}
                                        data-testid="ad-cancel-button"
                                        className="brutal-btn-white"
                                    >
                                        <X className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setEditing(true)}
                                    data-testid="ad-edit-button"
                                    className="brutal-btn-secondary"
                                >
                                    <Edit3 className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                    Edit advertising
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Side column - Comments */}
                <div className="lg:col-span-4">
                    <div data-testid="comments-section" className="brutal-card p-6 bg-white">
                        <div className="flex items-center gap-2 mb-5">
                            <div className="w-9 h-9 bg-vibrantorange border-[3px] border-ink rounded-xl flex items-center justify-center shadow-brutal">
                                <MessageCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
                            </div>
                            <h2 className="font-heading font-black text-xl text-ink tracking-tight">
                                Comments <span className="text-ink/40 font-bold">({comments.length})</span>
                            </h2>
                        </div>

                        <form onSubmit={onAddComment} className="mb-5" data-testid="comment-form">
                            {(!user || user === false) && (
                                <input
                                    data-testid="comment-guest-name-input"
                                    value={guestName}
                                    onChange={(e) => setGuestName(e.target.value)}
                                    placeholder="Your name (optional)"
                                    maxLength={24}
                                    className="brutal-input mb-3 text-sm"
                                />
                            )}
                            <textarea
                                data-testid="comment-input"
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder={user && user !== false ? "Drop a comment..." : "Comment as a guest..."}
                                rows={3}
                                maxLength={1000}
                                className="brutal-input resize-none"
                            />
                            <button
                                type="submit"
                                disabled={submittingComment || !newComment.trim()}
                                className="brutal-btn-primary mt-3 w-full"
                                data-testid="comment-submit-button"
                            >
                                <Send className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                {submittingComment ? "Posting..." : "Post comment"}
                            </button>
                            {(!user || user === false) && (
                                <p className="mt-2 text-xs text-ink/50 font-body text-center">
                                    <Link to="/login" className="font-heading font-bold underline decoration-vibrantorange decoration-[2px] underline-offset-2">
                                        Log in
                                    </Link>{" "}
                                    to comment under your username
                                </p>
                            )}
                        </form>

                        <div className="space-y-4" data-testid="comments-list">
                            {comments.length === 0 ? (
                                <div data-testid="comments-empty" className="text-center py-6">
                                    <p className="font-body text-ink/50">No comments yet. Be the first!</p>
                                </div>
                            ) : (
                                comments.map((c, idx) => {
                                    const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                                    const isMyComment = user && user !== false && user.id === c.user_id;
                                    return (
                                        <div key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3">
                                            <div className={`flex-shrink-0 w-9 h-9 ${color} border-[3px] border-ink rounded-full flex items-center justify-center`}>
                                                <User className="w-4 h-4 text-ink" strokeWidth={3} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="bg-cream border-[3px] border-ink rounded-2xl rounded-tl-sm p-3 shadow-brutal">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="font-heading font-bold text-sm text-ink">@{c.username}</span>
                                                        {isMyComment && (
                                                            <button
                                                                onClick={() => onDeleteComment(c.id)}
                                                                data-testid={`comment-delete-${c.id}`}
                                                                className="text-ink/40 hover:text-vibrantpink transition-colors"
                                                                title="Delete comment"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="font-body text-sm text-ink/90 mt-1 whitespace-pre-line break-words">
                                                        {c.text}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
